import { useMemo, useState, type FormEvent } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AUDIT_ACTION_LABELS } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { AuditEntries, downloadBlob } from '../../components/audit/AuditEntries';

/** Action families for the filter (the part before the first '.'), e.g. "payment." → Payment. */
const FAMILIES = [...new Set(Object.keys(AUDIT_ACTION_LABELS).map((a) => a.split('.')[0]!))]
  .filter((f) => f !== 'session')
  .sort();

interface Filters {
  actorUserId: string;
  subject: string;
  action: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { actorUserId: '', subject: '', action: '', from: '', to: '' };

/**
 * 0.26.0 — the organization's audit log (owner-only): who did what, when, filtered by person (did
 * it), subject (about), action family and date range; newest first with "Load more"; .xlsx export of
 * the filtered view. Per-person timelines live at /admin/activity/:kind/:id.
 */
export function AdminAuditLog() {
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const staff = useQuery({ queryKey: ['admin', 'staff'], queryFn: () => api.staff.list() });
  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api.tenants.list() });
  const contractors = useQuery({ queryKey: ['admin', 'contractors'], queryFn: () => api.contractors.list() });
  const leases = useQuery({ queryKey: ['admin', 'leases'], queryFn: () => api.leases.list() });

  const query = useInfiniteQuery({
    queryKey: ['admin', 'audit', applied],
    queryFn: ({ pageParam }) => api.audit.list({ ...applied, cursor: pageParam, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // "Did it" choices = every login (staff/owner, tenant portal logins, contractor logins).
  const actors = useMemo(() => {
    const out: Array<{ id: string; label: string }> = [];
    for (const s of staff.data?.staff ?? []) out.push({ id: s.id, label: `${s.name} (${s.role === 'owner' ? 'owner' : 'manager'})` });
    for (const t of tenants.data?.tenants ?? []) if (t.userId) out.push({ id: t.userId, label: `${t.firstName} ${t.lastName} (tenant)` });
    for (const c of contractors.data?.contractors ?? []) if (c.userId) out.push({ id: c.userId, label: `${c.companyName} (contractor)` });
    return out;
  }, [staff.data, tenants.data, contractors.data]);

  const subjects = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    for (const t of tenants.data?.tenants ?? []) out.push({ value: `tenant:${t.id}`, label: `Tenant: ${t.firstName} ${t.lastName}` });
    for (const s of staff.data?.staff ?? []) out.push({ value: `staff:${s.id}`, label: `Staff: ${s.name}` });
    for (const c of contractors.data?.contractors ?? []) out.push({ value: `contractor:${c.id}`, label: `Contractor: ${c.companyName}` });
    for (const l of leases.data?.leases ?? []) out.push({ value: `lease:${l.id}`, label: `Lease: ${l.unitLabel ?? l.unitId} (${l.startDate})` });
    return out;
  }, [tenants.data, staff.data, contractors.data, leases.data]);

  function apply(e: FormEvent) {
    e.preventDefault();
    setApplied(draft);
  }

  async function exportXlsx() {
    setExportErr(null);
    setExporting(true);
    try {
      downloadBlob(await api.audit.exportXlsx(applied), `audit-log-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e2) {
      setExportErr(errorMessage(e2));
    } finally {
      setExporting(false);
    }
  }

  const set = (k: keyof Filters) => (e: { target: { value: string } }) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h1>Audit log</h1>
        <button type="button" className="secondary" disabled={exporting} onClick={() => void exportXlsx()}>
          {exporting ? 'Exporting…' : 'Export .xlsx'}
        </button>
      </div>
      <p className="subtitle">
        Every recorded action in this account, newest first. Values, secrets and full addresses are never shown — only
        what happened, who did it, and which fields changed. Viewing this page is itself recorded.
      </p>

      <form className="card stack" onSubmit={apply}>
        <div className="grid">
          <div>
            <label htmlFor="au-actor">Who did it</label>
            <select id="au-actor" value={draft.actorUserId} onChange={set('actorUserId')}>
              <option value="">Anyone</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="au-subject">About</label>
            <select id="au-subject" value={draft.subject} onChange={set('subject')}>
              <option value="">Anything</option>
              {subjects.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="au-action">Kind of action</label>
            <select id="au-action" value={draft.action} onChange={set('action')}>
              <option value="">All</option>
              {FAMILIES.map((f) => (
                <option key={f} value={`${f}.`}>
                  {f.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="au-from">From</label>
            <input id="au-from" type="date" value={draft.from} onChange={set('from')} />
          </div>
          <div>
            <label htmlFor="au-to">To</label>
            <input id="au-to" type="date" value={draft.to} onChange={set('to')} />
          </div>
        </div>
        <div className="row">
          <button type="submit">Apply filters</button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setDraft(EMPTY);
              setApplied(EMPTY);
            }}
          >
            Clear
          </button>
        </div>
      </form>

      {exportErr && <StatusMessage tone="err">{exportErr}</StatusMessage>}
      {query.isLoading ? (
        <Loading label="Loading audit log…" />
      ) : query.isError || !query.data ? (
        <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <AuditEntries items={query.data.pages.flatMap((p) => p.items)} />
          {query.hasNextPage && (
            <p>
              <button type="button" className="secondary" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </p>
          )}
        </>
      )}
    </>
  );
}
